/**
 * Countries and their international dialling codes.
 *
 * This exists because a phone number became an identity. The public booking
 * portal signs a customer in by messaging their mobile, which means the number
 * has to reach Meta as full international digits — and it means `01211680705`
 * and `+201211680705` have to stop being two different people. A dial-code
 * picker, pre-set to the salon's own country, is what makes what gets stored
 * unambiguous at the moment it is typed.
 *
 * The list is stored compactly (`ISO|dial|English|Arabic`) rather than as 200
 * object literals: it is data, it is read far more often than it is edited, and
 * a wall of braces hides a wrong dial code better than a column of them does.
 * The Arabic name is optional — the countries a Gulf portal actually serves
 * have one, and the long tail falls back to English rather than to nothing.
 *
 * `pos-api/src/utils/countries.ts` holds the dial codes ONLY, as a deliberate
 * second copy: pos-api takes no workspace dependency (the same arrangement
 * money.ts has). If you add or correct a country here, correct it there too.
 *
 * Dial codes are ITU-T E.164 assignments. Several are shared — +1 covers the
 * NANP, +7 covers Russia and Kazakhstan — which is exactly why the stored value
 * is the ISO code and not the dial code.
 */

export interface Country {
  /** ISO-3166-1 alpha-2, upper case. */
  iso: string;
  /** International dialling code, digits only, no '+'. */
  dial: string;
  name: string;
  nameAr: string;
  /** Regional-indicator pair, derived from the ISO code. */
  flag: string;
}

// prettier-ignore
const DATA = [
  'AF|93|Afghanistan|أفغانستان', 'AL|355|Albania|ألبانيا', 'DZ|213|Algeria|الجزائر',
  'AD|376|Andorra|أندورا', 'AO|244|Angola|أنغولا', 'AR|54|Argentina|الأرجنتين',
  'AM|374|Armenia|أرمينيا', 'AU|61|Australia|أستراليا', 'AT|43|Austria|النمسا',
  'AZ|994|Azerbaijan|أذربيجان', 'BH|973|Bahrain|البحرين', 'BD|880|Bangladesh|بنغلاديش',
  'BY|375|Belarus|بيلاروسيا', 'BE|32|Belgium|بلجيكا', 'BJ|229|Benin|بنين',
  'BT|975|Bhutan|بوتان', 'BO|591|Bolivia|بوليفيا', 'BA|387|Bosnia and Herzegovina|البوسنة والهرسك',
  'BW|267|Botswana|بوتسوانا', 'BR|55|Brazil|البرازيل', 'BN|673|Brunei|بروناي',
  'BG|359|Bulgaria|بلغاريا', 'BF|226|Burkina Faso|بوركينا فاسو', 'BI|257|Burundi|بوروندي',
  'KH|855|Cambodia|كمبوديا', 'CM|237|Cameroon|الكاميرون', 'CA|1|Canada|كندا',
  'CV|238|Cape Verde|الرأس الأخضر', 'CF|236|Central African Republic|أفريقيا الوسطى',
  'TD|235|Chad|تشاد', 'CL|56|Chile|تشيلي', 'CN|86|China|الصين', 'CO|57|Colombia|كولومبيا',
  'KM|269|Comoros|جزر القمر', 'CG|242|Congo|الكونغو', 'CD|243|Congo (DRC)|الكونغو الديمقراطية',
  'CR|506|Costa Rica|كوستاريكا', 'CI|225|Côte d’Ivoire|ساحل العاج', 'HR|385|Croatia|كرواتيا',
  'CU|53|Cuba|كوبا', 'CY|357|Cyprus|قبرص', 'CZ|420|Czechia|التشيك', 'DK|45|Denmark|الدنمارك',
  'DJ|253|Djibouti|جيبوتي', 'DO|1|Dominican Republic|الدومينيكان', 'EC|593|Ecuador|الإكوادور',
  'EG|20|Egypt|مصر', 'SV|503|El Salvador|السلفادور', 'GQ|240|Equatorial Guinea|غينيا الاستوائية',
  'ER|291|Eritrea|إريتريا', 'EE|372|Estonia|إستونيا', 'ET|251|Ethiopia|إثيوبيا',
  'FI|358|Finland|فنلندا', 'FR|33|France|فرنسا', 'GA|241|Gabon|الغابون', 'GM|220|Gambia|غامبيا',
  'GE|995|Georgia|جورجيا', 'DE|49|Germany|ألمانيا', 'GH|233|Ghana|غانا', 'GR|30|Greece|اليونان',
  'GT|502|Guatemala|غواتيمالا', 'GN|224|Guinea|غينيا', 'GY|592|Guyana|غيانا', 'HT|509|Haiti|هايتي',
  'HN|504|Honduras|هندوراس', 'HK|852|Hong Kong|هونغ كونغ', 'HU|36|Hungary|المجر',
  'IS|354|Iceland|آيسلندا', 'IN|91|India|الهند', 'ID|62|Indonesia|إندونيسيا', 'IR|98|Iran|إيران',
  'IQ|964|Iraq|العراق', 'IE|353|Ireland|أيرلندا', 'IL|972|Israel|إسرائيل', 'IT|39|Italy|إيطاليا',
  'JM|1|Jamaica|جامايكا', 'JP|81|Japan|اليابان', 'JO|962|Jordan|الأردن', 'KZ|7|Kazakhstan|كازاخستان',
  'KE|254|Kenya|كينيا', 'KW|965|Kuwait|الكويت', 'KG|996|Kyrgyzstan|قيرغيزستان', 'LA|856|Laos|لاوس',
  'LV|371|Latvia|لاتفيا', 'LB|961|Lebanon|لبنان', 'LS|266|Lesotho|ليسوتو', 'LR|231|Liberia|ليبيريا',
  'LY|218|Libya|ليبيا', 'LI|423|Liechtenstein|ليختنشتاين', 'LT|370|Lithuania|ليتوانيا',
  'LU|352|Luxembourg|لوكسمبورغ', 'MO|853|Macao|ماكاو', 'MG|261|Madagascar|مدغشقر',
  'MW|265|Malawi|مالاوي', 'MY|60|Malaysia|ماليزيا', 'MV|960|Maldives|المالديف', 'ML|223|Mali|مالي',
  'MT|356|Malta|مالطا', 'MR|222|Mauritania|موريتانيا', 'MU|230|Mauritius|موريشيوس',
  'MX|52|Mexico|المكسيك', 'MD|373|Moldova|مولدوفا', 'MC|377|Monaco|موناكو', 'MN|976|Mongolia|منغوليا',
  'ME|382|Montenegro|الجبل الأسود', 'MA|212|Morocco|المغرب', 'MZ|258|Mozambique|موزمبيق',
  'MM|95|Myanmar|ميانمار', 'NA|264|Namibia|ناميبيا', 'NP|977|Nepal|نيبال',
  'NL|31|Netherlands|هولندا', 'NZ|64|New Zealand|نيوزيلندا', 'NI|505|Nicaragua|نيكاراغوا',
  'NE|227|Niger|النيجر', 'NG|234|Nigeria|نيجيريا', 'KP|850|North Korea|كوريا الشمالية',
  'MK|389|North Macedonia|مقدونيا الشمالية', 'NO|47|Norway|النرويج', 'OM|968|Oman|عُمان',
  'PK|92|Pakistan|باكستان', 'PS|970|Palestine|فلسطين', 'PA|507|Panama|بنما',
  'PG|675|Papua New Guinea|بابوا غينيا الجديدة', 'PY|595|Paraguay|باراغواي', 'PE|51|Peru|بيرو',
  'PH|63|Philippines|الفلبين', 'PL|48|Poland|بولندا', 'PT|351|Portugal|البرتغال',
  'PR|1|Puerto Rico|بورتوريكو', 'QA|974|Qatar|قطر', 'RO|40|Romania|رومانيا', 'RU|7|Russia|روسيا',
  'RW|250|Rwanda|رواندا', 'SA|966|Saudi Arabia|السعودية', 'SN|221|Senegal|السنغال',
  'RS|381|Serbia|صربيا', 'SC|248|Seychelles|سيشل', 'SL|232|Sierra Leone|سيراليون',
  'SG|65|Singapore|سنغافورة', 'SK|421|Slovakia|سلوفاكيا', 'SI|386|Slovenia|سلوفينيا',
  'SO|252|Somalia|الصومال', 'ZA|27|South Africa|جنوب أفريقيا', 'KR|82|South Korea|كوريا الجنوبية',
  'SS|211|South Sudan|جنوب السودان', 'ES|34|Spain|إسبانيا', 'LK|94|Sri Lanka|سريلانكا',
  'SD|249|Sudan|السودان', 'SE|46|Sweden|السويد', 'CH|41|Switzerland|سويسرا', 'SY|963|Syria|سوريا',
  'TW|886|Taiwan|تايوان', 'TJ|992|Tajikistan|طاجيكستان', 'TZ|255|Tanzania|تنزانيا',
  'TH|66|Thailand|تايلاند', 'TG|228|Togo|توغو', 'TN|216|Tunisia|تونس', 'TR|90|Türkiye|تركيا',
  'TM|993|Turkmenistan|تركمانستان', 'UG|256|Uganda|أوغندا', 'UA|380|Ukraine|أوكرانيا',
  'AE|971|United Arab Emirates|الإمارات', 'GB|44|United Kingdom|المملكة المتحدة',
  'US|1|United States|الولايات المتحدة', 'UY|598|Uruguay|أوروغواي', 'UZ|998|Uzbekistan|أوزبكستان',
  'VE|58|Venezuela|فنزويلا', 'VN|84|Vietnam|فيتنام', 'YE|967|Yemen|اليمن', 'ZM|260|Zambia|زامبيا',
  'ZW|263|Zimbabwe|زيمبابوي',
];

/** ISO code → its two regional-indicator symbols, which render as a flag. */
function flagFor(iso: string): string {
  return String.fromCodePoint(
    ...[...iso.toUpperCase()].map((c) => 0x1f1e6 + (c.charCodeAt(0) - 65)),
  );
}

export const COUNTRIES: Country[] = DATA.map((entry) => {
  const [iso = '', dial = '', name = '', nameAr = ''] = entry.split('|');
  return { iso, dial, name, nameAr: nameAr || name, flag: flagFor(iso) };
});

const BY_ISO = new Map(COUNTRIES.map((c) => [c.iso, c]));

export function countryFor(iso: string | null | undefined): Country | null {
  return iso ? (BY_ISO.get(iso.trim().toUpperCase()) ?? null) : null;
}

export function dialCodeFor(iso: string | null | undefined): string | null {
  return countryFor(iso)?.dial ?? null;
}

/**
 * Combine a country and a locally-typed number into E.164 digits (no '+').
 *
 * The leading zero people write out of habit is a national trunk prefix and
 * never belongs after a country code — dropping it here is what makes
 * `0121 168 0705` under Egypt and `+20 121 168 0705` the same string, and
 * therefore the same account.
 *
 * Returns null when the result could not be a real number, so a caller can
 * refuse rather than send a code into the void.
 */
export function toE164(iso: string | null | undefined, localNumber: string): string | null {
  const typed = localNumber.replace(/\D/g, '');
  if (!typed) return null;

  // Already international, however it was written.
  if (localNumber.trim().startsWith('+')) {
    return typed.length >= 8 && typed.length <= 15 ? typed : null;
  }
  if (typed.startsWith('00')) {
    const rest = typed.slice(2);
    return rest.length >= 8 && rest.length <= 15 ? rest : null;
  }

  const dial = dialCodeFor(iso);
  if (!dial) return typed.length >= 8 && typed.length <= 15 ? typed : null;

  const national = typed.startsWith('0') ? typed.slice(1) : typed;
  if (!national) return null;
  // Someone who typed their own country code into the local field.
  const full = national.startsWith(dial) ? national : `${dial}${national}`;
  return full.length >= 8 && full.length <= 15 ? full : null;
}
